import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, CircleAlert, CircleCheck, Copy, Info, Languages, RefreshCw, Rocket, RotateCcw, Sparkles, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublishButton } from "@/components/publish";
import { toast } from "sonner";
import type { Build, BuildEvent, BuildWithEvents, NoteTarget } from "@releo/shared";
import { NOTES_MAX_LENGTH } from "@releo/shared/constants";
import { CommitPicker } from "@/components/commit-picker";
import { TranslateDialog } from "@/components/translate-dialog";
import { ConfirmDialog, Mono, PlatformIcon, StatusBadge, toastError } from "@/components/common";
import { Badge, Button, Card, CardHeader, Dialog, ErrorBox, Field, Input, Menu, MenuItem, Select, Spinner, Tabs, TabsContent, TabsList, TabsTrigger, Textarea } from "@/components/ui";
import { http } from "@/lib/api";
import { keys, useApp, useBuild, useChangelogBase, useInvalidateBuild } from "@/lib/queries";
import { cn, formatBytes, formatDate } from "@/lib/utils";

// ------------------------------------------------------------------ Notes editor

/** Where edited notes go right now (mirrors the API `notesSyncable` rule). */
function syncHint(build: Build, target: NoteTarget): "testflight" | "play" | "locked" | "promotion" | null {
  if (build.platform === "ios") {
    if (target === "testing") return build.status === "testing" && build.storeRef.ascBuildId ? "testflight" : null;
    return ["in_review", "pending_release", "released", "superseded"].includes(build.status) ? "locked" : "promotion";
  }
  if (target === "testing") return build.status === "testing" && build.track && build.track !== "production" ? "play" : null;
  return ["released", "submitted"].includes(build.status) && build.track === "production" ? "play" : "promotion";
}

function NotesEditor({ build, locales, canEdit, hasGit, hasLlm }: { build: BuildWithEvents; locales: string[]; canEdit: boolean; hasGit: boolean; hasLlm: boolean }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateBuild();
  const [target, setTarget] = useState<NoteTarget>("store");
  const allLocales = useMemo(() => [...new Set([...locales, ...build.notes.map((n) => n.locale)])], [locales, build.notes]);
  const fromBuild = useMemo(() => {
    const map: Record<string, string> = {};
    for (const n of build.notes) map[`${n.target}:${n.locale}`] = n.text;
    return map;
  }, [build.notes]);
  const [draft, setDraft] = useState<Record<string, string>>(fromBuild);
  const [saving, setSaving] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const copyPrevious = async () => {
    try {
      await http.post(`/builds/${build.id}/notes/copy-previous`);
      toast.success(t("build.copied"));
      invalidate(build);
    } catch (err) {
      toastError(err);
    }
  };
  useEffect(() => setDraft(fromBuild), [fromBuild]);

  const max = NOTES_MAX_LENGTH[build.platform][target];
  const dirty = allLocales.some((l) => (draft[`${target}:${l}`] ?? "") !== (fromBuild[`${target}:${l}`] ?? ""));

  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/builds/${build.id}/notes`, {
        notes: allLocales
          .filter((l) => (draft[`${target}:${l}`] ?? "") !== (fromBuild[`${target}:${l}`] ?? ""))
          .map((locale) => ({ locale, target, text: draft[`${target}:${locale}`] ?? "" })),
      });
      toast.success(t("common.saved"));
      invalidate(build);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("build.notes")}
        actions={
          canEdit && (
            <Menu
              trigger={
                <Button variant="outline" size="sm">
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("build.fill")}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              }
            >
              <MenuItem icon={<Sparkles className="h-4 w-4" />} disabled={!hasLlm || !hasGit} hint={!hasLlm || !hasGit ? t("build.fillNeedsLlmGit") : t("build.fillCommitsHint")} onSelect={() => setGenerateOpen(true)}>
                {t("build.fillCommits")}
              </MenuItem>
              <MenuItem icon={<Languages className="h-4 w-4" />} disabled={!hasLlm} hint={!hasLlm ? t("build.fillNeedsLlm") : t("build.fillTranslateHint")} onSelect={() => setTranslateOpen(true)}>
                {t("build.fillTranslate")}
              </MenuItem>
              {target === "store" && (
                <MenuItem icon={<Copy className="h-4 w-4" />} hint={t("build.fillCopyHint")} onSelect={copyPrevious}>
                  {t("build.fillCopy")}
                </MenuItem>
              )}
            </Menu>
          )
        }
      />
      <div className="p-5">
        <Tabs value={target} onValueChange={(v) => setTarget(v as NoteTarget)}>
          <TabsList>
            <TabsTrigger value="store">{t("build.storeNotes")}</TabsTrigger>
            <TabsTrigger value="testing">{t("build.testingNotes")}</TabsTrigger>
          </TabsList>
          {(["store", "testing"] as const).map((tg) => (
            <TabsContent key={tg} value={tg} className="mt-4 space-y-4">
              <p className="text-xs text-fg-muted">{t(tg === "store" ? "build.storeNotesHelp" : "build.testingNotesHelp")}</p>
              {syncHint(build, tg) && (
                <p className={cn("rounded-md px-3 py-2 text-xs", syncHint(build, tg) === "locked" ? "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300" : "bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-300")}>
                  {t(`build.sync.${syncHint(build, tg)}`, { track: build.track })}
                </p>
              )}
              {allLocales.map((locale) => {
                const key = `${tg}:${locale}`;
                const value = draft[key] ?? "";
                const source = build.notes.find((n) => n.target === tg && n.locale === locale)?.source;
                return (
                  <div key={key} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label htmlFor={key} className="flex items-center gap-2 text-sm font-medium">
                        {locale}
                        {source && <Badge tone={source === "llm" ? "violet" : source === "copied" ? "blue" : "neutral"}>{t(`build.source.${source}`)}</Badge>}
                      </label>
                      <span className={cn("text-xs", value.length > max ? "text-red-600" : "text-fg-muted")}>{t("build.chars", { count: value.length, max })}</span>
                    </div>
                    <Textarea id={key} rows={4} value={value} disabled={!canEdit} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
                  </div>
                );
              })}
              {canEdit && (
                <div className="flex justify-end">
                  <Button onClick={save} loading={saving} disabled={!dirty}>
                    {t("build.saveNotes")}
                  </Button>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      <GenerateDialog open={generateOpen} onOpenChange={setGenerateOpen} build={build} target={target} locales={allLocales} hasGit={hasGit} />
      <TranslateDialog
        open={translateOpen}
        onOpenChange={setTranslateOpen}
        locales={allLocales}
        texts={Object.fromEntries(allLocales.map((l) => [l, draft[`${target}:${l}`] ?? ""]))}
        onTranslate={async (body) => {
          await http.post(`/builds/${build.id}/notes/generate`, { ...body, mode: "translate", target });
          invalidate(build);
        }}
      />
    </Card>
  );
}

function GenerateDialog({
  open,
  onOpenChange,
  build,
  target,
  locales,
  hasGit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  build: Build;
  target: NoteTarget;
  locales: string[];
  hasGit: boolean;
}) {
  const { t } = useTranslation();
  const invalidate = useInvalidateBuild();
  const base = useChangelogBase(build.id, open);
  const [mode, setMode] = useState<"commits" | "template">("commits");
  const [headCommit, setHeadCommit] = useState(build.commitSha ?? "");
  const [baseCommit, setBaseCommit] = useState("");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => setHeadCommit(build.commitSha ?? ""), [build.commitSha]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await http.post<{ commitCount: number }>(`/builds/${build.id}/notes/generate`, {
        mode,
        target,
        locales,
        headCommit: headCommit || undefined,
        baseCommit: baseCommit || undefined,
        instructions: instructions || undefined,
      });
      toast.success(t("build.generated", { count: res.commitCount }));
      invalidate(build);
      onOpenChange(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("build.fillCommits")}
      description={`${t(target === "store" ? "build.storeNotes" : "build.testingNotes")} · ${locales.join(", ")}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={run} loading={busy}>
            <Sparkles className="h-4 w-4" />
            {busy ? t("build.generating") : t("build.generate")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t("build.generateMode")} htmlFor="gen-mode">
          <Select id="gen-mode" value={mode} onChange={(e) => setMode(e.target.value as "commits" | "template")}>
            <option value="commits">{t("build.modeCommits")}</option>
            <option value="template">{t("build.modeTemplate")}</option>
          </Select>
        </Field>
        <Field label={t("build.headCommit")} help={build.commitSha ? t("build.headCommitHelpSet") : t("build.headCommitHelp")} htmlFor="gen-sha">
          <CommitPicker appId={build.appId} hasGit={hasGit} value={headCommit} onChange={setHeadCommit} autoSelectLatest={!build.commitSha} idPrefix="gen" />
        </Field>
        <div className="rounded-md bg-muted/60 px-3 py-2 text-xs text-fg-muted">
          {base.data?.build
            ? t("build.baseFrom", { version: base.data.build.version, build: base.data.build.buildNumber, sha: base.data.build.commitSha.slice(0, 8) })
            : t("build.baseNone")}{" "}
          {t("build.baseExplain")}
        </div>
        <Field label={<>{t("build.baseCommit")} <span className="font-normal text-fg-muted">({t("common.optional")})</span></>} help={t("build.baseCommitHelp")} htmlFor="gen-base">
          <Input id="gen-base" value={baseCommit} onChange={(e) => setBaseCommit(e.target.value)} className="font-mono" placeholder={base.data?.build?.commitSha.slice(0, 8)} />
        </Field>
        <Field label={t("build.instructions")} htmlFor="gen-instr">
          <Textarea id="gen-instr" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </Field>
        <ErrorBox error={error} />
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Promote

function PromoteDialog({ build, iosReleaseMode, androidRollout, open, onOpenChange }: { build: Build; iosReleaseMode: "manual" | "after_approval"; androidRollout: number; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateBuild();
  const [releaseMode, setReleaseMode] = useState(iosReleaseMode);
  const [track, setTrack] = useState<"production" | "beta" | "alpha">("production");
  const [rollout, setRollout] = useState(Math.round(androidRollout * 100));
  const [busy, setBusy] = useState(false);
  const ios = build.platform === "ios";

  const run = async () => {
    setBusy(true);
    try {
      await http.post(`/builds/${build.id}/promote`, ios ? { releaseMode } : { track, rollout: Math.min(100, Math.max(1, rollout)) / 100 });
      toast.success(t("build.promoted"));
      invalidate(build);
      onOpenChange(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={ios ? t("build.promoteIos") : t("build.promote")}
      description={ios ? t("build.promoteIosHelp", { version: build.version }) : t("build.promoteAndroidHelp", { from: build.track ?? "internal" })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={run} loading={busy}>
            <Rocket className="h-4 w-4" />
            {t("common.confirm")}
          </Button>
        </>
      }
    >
      {ios ? (
        <Field label={t("build.releaseMode")} htmlFor="release-mode">
          <Select id="release-mode" value={releaseMode} onChange={(e) => setReleaseMode(e.target.value as "manual" | "after_approval")}>
            <option value="manual">{t("build.releaseModeManual")}</option>
            <option value="after_approval">{t("build.releaseModeAuto")}</option>
          </Select>
        </Field>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("build.targetTrack")} htmlFor="track">
            <Select id="track" value={track} onChange={(e) => setTrack(e.target.value as typeof track)}>
              <option value="production">production</option>
              <option value="beta">beta</option>
              <option value="alpha">alpha</option>
            </Select>
          </Field>
          <Field label={t("build.rollout")} htmlFor="rollout">
            <Input id="rollout" type="number" min={1} max={100} value={rollout} onChange={(e) => setRollout(Number(e.target.value))} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ Timeline

function EventIcon({ level }: { level: BuildEvent["level"] }) {
  if (level === "error") return <CircleAlert className="h-4 w-4 text-red-600" />;
  if (level === "warn") return <TriangleAlert className="h-4 w-4 text-amber-500" />;
  return <CircleCheck className="h-4 w-4 text-emerald-600" />;
}

function Timeline({ events }: { events: BuildEvent[] }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  if (!events.length) return <p className="text-sm text-fg-muted">{t("build.noEvents")}</p>;
  return (
    <ol className="space-y-3">
      {[...events].reverse().map((e) => {
        const data = e.data as { stdout?: string; stderr?: string } | null;
        const hasOutput = !!(data && (data.stdout || data.stderr));
        return (
          <li key={e.id} className="flex gap-3">
            <div className="mt-0.5 shrink-0">
              <EventIcon level={e.level} />
            </div>
            <div className="min-w-0 flex-1">
              <p className={cn("break-words text-sm", e.message.startsWith("$ ") && "font-mono text-xs")}>{e.message}</p>
              <p className="text-xs text-fg-muted">
                {e.step} · {formatDate(e.createdAt, i18n.language)}
                {hasOutput && (
                  <button className="ml-2 underline" onClick={() => setOpen(open === e.id ? null : e.id)}>
                    {open === e.id ? "−" : "+"} output
                  </button>
                )}
              </p>
              {open === e.id && hasOutput && (
                <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-200">{[data?.stderr, data?.stdout].filter(Boolean).join("\n")}</pre>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------------------ Page

export function BuildPage({ appId, buildId }: { appId: string; buildId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const build = useBuild(buildId);
  const app = useApp(appId);
  const invalidate = useInvalidateBuild();
  const [promoteOpen, setPromoteOpen] = useState(false);

  if (build.isLoading || app.isLoading) return <Spinner />;
  if (build.error || !build.data || !app.data) return <ErrorBox error={build.error ?? app.error ?? new Error(t("build.notFound"))} />;
  const b = build.data;
  const canManage = app.data.myRole === "manager";
  const canPromote = canManage && (b.status === "testing" || (b.platform === "ios" && b.status === "rejected"));
  const action = async (path: string) => {
    try {
      await http.post(`/builds/${b.id}/${path}`);
      invalidate(b);
    } catch (err) {
      toastError(err);
    }
  };

  const details: [string, React.ReactNode][] = [
    [t("build.status"), <StatusBadge key="s" status={b.status} />],
    [t("build.track"), b.track ?? "—"],
    [t("build.commit"), b.commitSha ? <Mono key="c">{b.commitSha.slice(0, 12)}</Mono> : "—"],
    [t("build.size"), b.fileAvailable ? formatBytes(b.fileSize) : <span key="f" className="text-fg-muted">{t("build.fileDeleted")}</span>],
    [t("build.uploadedBy"), b.uploadedBy?.name ?? "—"],
    [t("build.uploadedAt"), formatDate(b.createdAt, i18n.language)],
  ];
  if (b.promotedAt) details.push([t("build.promotedAt"), formatDate(b.promotedAt, i18n.language)]);
  if (typeof b.storeRef.ascVersionState === "string") details.push(["App Store", <Mono key="v">{b.storeRef.ascVersionState}</Mono>]);

  return (
    <>
      <Link to="/apps/$appId" params={{ appId }} className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" />
        {app.data.name}
      </Link>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <PlatformIcon platform={b.platform} className="h-5 w-5" />
          </div>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              {b.version} <span className="font-normal text-fg-muted">({b.buildNumber})</span>
            </h1>
            <p className="font-mono text-xs text-fg-muted">{b.fileName}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && <PublishButton app={app.data} build={b} />}
          {["processing", "in_review", "pending_release"].includes(b.status) && b.platform === "ios" && (
            <Button variant="outline" onClick={() => action("refresh")}>
              <RefreshCw className="h-4 w-4" />
              {t("common.refresh")}
            </Button>
          )}
          {canManage && b.status === "failed" && (
            <Button variant="outline" onClick={() => action("retry")}>
              <RotateCcw className="h-4 w-4" />
              {t("build.retry")}
            </Button>
          )}
          {canManage && b.status === "pending_release" && (
            <ConfirmDialog
              title={t("build.release")}
              description={t("build.releaseHelp")}
              onConfirm={() => action("release")}
              trigger={(open) => (
                <Button onClick={open}>
                  <Rocket className="h-4 w-4" />
                  {t("build.release")}
                </Button>
              )}
            />
          )}
          {canPromote && (
            <Button onClick={() => setPromoteOpen(true)}>
              <Rocket className="h-4 w-4" />
              {b.platform === "ios" ? t("build.promoteIos") : t("build.promote")}
            </Button>
          )}
          {canManage && !["pushing", "processing", "promoting", "in_review"].includes(b.status) && (
            <ConfirmDialog
              danger
              title={t("build.delete")}
              description={t("build.deleteHelp")}
              confirmLabel={t("common.delete")}
              onConfirm={async () => {
                await http.del(`/builds/${b.id}`);
                void qc.invalidateQueries({ queryKey: ["apps", appId, "builds"] });
                qc.removeQueries({ queryKey: keys.build(b.id) });
                await navigate({ to: "/apps/$appId", params: { appId } });
              }}
              trigger={(open) => (
                <Button variant="ghost" size="icon" onClick={open} aria-label={t("build.delete")}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            />
          )}
        </div>
      </div>

      {b.error && (
        <div className="mb-6 flex gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="break-words">{b.error}</span>
        </div>
      )}

      {b.status === "submitted" && (
        <div className="mb-6 flex gap-2 rounded-md border bg-muted/40 px-4 py-3 text-sm text-fg-muted">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t("build.submittedHelp")}</span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-6">
          <NotesEditor build={b} locales={app.data.locales} canEdit={canManage} hasGit={!!app.data.gitProviderId && !!app.data.gitRepo} hasLlm={!!app.data.llmProviderId} />
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader title={t("build.details")} />
            <dl className="divide-y text-sm">
              {details.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 px-5 py-2.5">
                  <dt className="text-fg-muted">{k}</dt>
                  <dd className="text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </Card>
          <Card>
            <CardHeader title={t("build.timeline")} />
            <div className="p-5">
              <Timeline events={b.events} />
            </div>
          </Card>
        </div>
      </div>
      <PromoteDialog build={b} iosReleaseMode={app.data.iosReleaseMode} androidRollout={app.data.androidRollout} open={promoteOpen} onOpenChange={setPromoteOpen} />
    </>
  );
}
